import { useEffect, useState, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { openStorageFile } from "@/lib/openStorageFile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Pencil, Calendar, Clock, Upload, Paperclip, X, Save, Link2, ExternalLink } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "";

const emptyForm = () => ({ learner_ids: [], session_id: null, title: "", description: "", publish_date: today(), deadline: "", attachment_url: "", attachment_path: "", attachment_name: "" });

export default function CoachMissions() {
  const [items, setItems] = useState([]);
  const [learners, setLearners] = useState([]);
  const [createSessions, setCreateSessions] = useState([]);
  const [editSessions, setEditSessions] = useState([]);
  const [filters, setFilters] = useState({ learner_id: "all", status: "all" });
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const createFileRef = useRef();
  const editFileRef = useRef();

  const uploadAttachment = async (e, setter) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/coach/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter((p) => ({ ...p, attachment_path: data.path, attachment_name: f.name }));
      toast.success("Fichier joint");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const openUrl = (u) => window.open(u, "_blank", "noopener,noreferrer");
  const openFile = async (p) => { try { await openStorageFile(p); } catch { toast.error("Impossible d'ouvrir le fichier"); } };

  const load = () => {
    const params = {};
    if (filters.learner_id !== "all") params.learner_id = filters.learner_id;
    if (filters.status !== "all") params.status = filters.status;
    return api.get("/coach/missions", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [filters]);
  const loadTemplates = () => api.get("/coach/mission-templates").then(({ data }) => setTemplates(data));
  useEffect(() => { api.get("/coach/learners").then(({ data }) => setLearners(data)); loadTemplates(); }, []);

  const applyTemplate = (t) => setForm((p) => {
    let session_id = p.session_id;
    if (t.session_number && p.learner_ids.length === 1) {
      const s = createSessions.find((x) => x.number === t.session_number);
      if (s) session_id = s.id;
    }
    return {
      ...p, title: t.title, description: t.description || "",
      attachment_url: t.attachment_url || "", attachment_path: t.attachment_path || "", attachment_name: t.attachment_name || "",
      session_id,
    };
  });
  const saveTemplate = async () => {
    if (!form.title.trim()) return toast.error("Ajoutez un titre d'abord");
    let session_number = null, session_title = "";
    if (form.session_id) {
      const s = createSessions.find((x) => x.id === form.session_id);
      if (s) { session_number = s.number; session_title = s.title; }
    }
    await api.post("/coach/mission-templates", {
      title: form.title, description: form.description,
      attachment_url: form.attachment_url || null,
      attachment_path: form.attachment_path || null,
      attachment_name: form.attachment_name || "",
      session_number, session_title,
    });
    toast.success("Modèle enregistré");
    loadTemplates();
  };
  const deleteSelectedTemplate = async () => {
    if (!selectedTemplateId) return;
    if (!window.confirm("Supprimer ce modèle ?")) return;
    await api.delete(`/coach/mission-templates/${selectedTemplateId}`);
    setSelectedTemplateId("");
    loadTemplates();
  };
  const groupedTemplates = useMemo(() => {
    const groups = {};
    templates.forEach((t) => {
      const k = t.session_number || 0;
      (groups[k] = groups[k] || []).push(t);
    });
    return Object.keys(groups).map(Number).sort((a, b) => a - b).map((k) => ({
      key: k,
      label: k === 0 ? "Sans séance" : `Séance ${k}${groups[k][0].session_title ? " — " + groups[k][0].session_title : ""}`,
      items: groups[k],
    }));
  }, [templates]);

  const singleLearner = form.learner_ids.length === 1 ? form.learner_ids[0] : null;
  useEffect(() => {
    if (singleLearner) api.get(`/coach/learners/${singleLearner}/sessions`).then(({ data }) => setCreateSessions(data));
    else setCreateSessions([]);
  }, [singleLearner]);

  useEffect(() => {
    if (editing?.learner_id) api.get(`/coach/learners/${editing.learner_id}/sessions`).then(({ data }) => setEditSessions(data));
    else setEditSessions([]);
  }, [editing?.learner_id]);

  const toggleLearner = (id) => {
    setForm((p) => {
      const has = p.learner_ids.includes(id);
      const learner_ids = has ? p.learner_ids.filter((x) => x !== id) : [...p.learner_ids, id];
      return { ...p, learner_ids, session_id: learner_ids.length === 1 ? p.session_id : null };
    });
  };
  const allSelected = learners.length > 0 && form.learner_ids.length === learners.length;
  const toggleAll = () => setForm((p) => ({ ...p, learner_ids: allSelected ? [] : learners.map((l) => l.id), session_id: null }));

  const openCreate = () => { setForm(emptyForm()); setSelectedTemplateId(""); setOpen(true); };

  const duplicate = (m) => {
    setSelectedTemplateId("");
    setForm({
      learner_ids: [], session_id: null,
      title: m.title, description: m.description || "",
      publish_date: m.publish_date || today(), deadline: m.deadline || "",
      attachment_url: m.attachment_url || "", attachment_path: m.attachment_path || "", attachment_name: m.attachment_name || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.learner_ids.length === 0) return toast.error("Choisissez au moins un apprenant");
    try {
      const payload = {
        learner_ids: form.learner_ids,
        session_id: form.learner_ids.length === 1 ? (form.session_id || null) : null,
        title: form.title, description: form.description,
        publish_date: form.publish_date || today(),
        deadline: form.deadline || null,
        attachment_url: form.attachment_url || null,
        attachment_path: form.attachment_path || null,
        attachment_name: form.attachment_name || "",
      };
      const { data } = await api.post("/coach/missions", payload);
      toast.success(data.count > 1 ? `Mission assignée à ${data.count} apprenants` : "Mission créée");
      setOpen(false); setForm(emptyForm());
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/coach/missions/${editing.id}`, {
        title: editing.title, description: editing.description || "",
        session_id: editing.session_id || null,
        publish_date: editing.publish_date || today(),
        deadline: editing.deadline || null,
        attachment_url: editing.attachment_url || null,
        attachment_path: editing.attachment_path || null,
        attachment_name: editing.attachment_name || "",
      });
      toast.success("Mission modifiée");
      setEditing(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const toggle = async (m) => { await api.patch(`/coach/missions/${m.id}`, { completed: !m.completed }); load(); };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette mission ?")) return;
    await api.delete(`/coach/missions/${id}`); load();
  };

  const learnerName = (id) => { const l = learners.find((x) => x.id === id); return l ? `${l.first_name} ${l.last_name}` : ""; };

  const sorted = [...items].sort((a, b) => {
    if (sort === "learner") return learnerName(a.learner_id).localeCompare(learnerName(b.learner_id), "fr", { sensitivity: "base" });
    if (sort === "publish") return (b.publish_date || "").localeCompare(a.publish_date || "");
    if (sort === "deadline") {
      if (!a.deadline) return 1; if (!b.deadline) return -1;
      return a.deadline.localeCompare(b.deadline);
    }
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl">Missions</h1>
          <p className="text-[#5C6561] mt-1">Assignez des tâches à un ou plusieurs apprenants.</p>
        </div>
        <Button data-testid="create-mission-button" onClick={openCreate} className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle mission
        </Button>
      </div>

      {/* Create / Duplicate dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Créer une mission</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Apprenant(s)</Label>
                <button type="button" onClick={toggleAll} className="text-xs text-[#D95D39] font-medium" data-testid="mission-select-all">
                  {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
              </div>
              <div className="border border-[#E6E1DA] rounded-lg max-h-40 overflow-y-auto divide-y divide-[#F0EBE4]" data-testid="mission-learner-list">
                {learners.length === 0 && <div className="p-3 text-sm text-[#5C6561]">Aucun apprenant.</div>}
                {learners.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#FAF8F5]" data-testid={`mission-learner-${l.id}`}>
                    <Checkbox checked={form.learner_ids.includes(l.id)} onCheckedChange={() => toggleLearner(l.id)} />
                    <span className="text-sm">{l.first_name} {l.last_name}</span>
                  </label>
                ))}
              </div>
              {form.learner_ids.length > 1 && (
                <p className="text-[11px] text-[#5C6561] mt-1">{form.learner_ids.length} apprenants — la séance associée est désactivée pour un envoi groupé.</p>
              )}
            </div>
            {form.learner_ids.length === 1 && (
              <div><Label>Séance associée (optionnel)</Label>
                <Select value={form.session_id || "none"} onValueChange={(v) => setForm({ ...form, session_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {createSessions.map(s => <SelectItem key={s.id} value={s.id}>Séance {s.number} — {s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {templates.length > 0 && (
              <div data-testid="mission-templates">
                <Label className="text-xs text-[#5C6561]">Charger un modèle</Label>
                <div className="flex items-end gap-2 mt-1">
                  <div className="flex-1">
                    <Select value={selectedTemplateId} onValueChange={(v) => { setSelectedTemplateId(v); const t = templates.find((x) => x.id === v); if (t) applyTemplate(t); }}>
                      <SelectTrigger data-testid="mission-template-select"><SelectValue placeholder="Choisir un modèle…" /></SelectTrigger>
                      <SelectContent>
                        {groupedTemplates.map((g) => (
                          <SelectGroup key={g.key}>
                            <SelectLabel>{g.label}</SelectLabel>
                            {g.items.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`mission-template-${t.id}`}>{t.title}</SelectItem>)}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedTemplateId && (
                    <Button type="button" variant="ghost" size="icon" onClick={deleteSelectedTemplate} data-testid="delete-template-button" title="Supprimer ce modèle">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div><Label>Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mission-form-title" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <button type="button" onClick={saveTemplate} className="text-xs text-[#2A5B52] font-medium inline-flex items-center gap-1" data-testid="save-template-button">
              <Save className="w-3 h-3" /> Enregistrer comme modèle
            </button>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de publication</Label>
                <Input type="date" value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} data-testid="mission-form-publish" />
              </div>
              <div><Label>Date de rendu (facultatif)</Label>
                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="mission-form-deadline" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pièces jointes (facultatif)</Label>
              <Input value={form.attachment_url || ""} onChange={(e) => setForm({ ...form, attachment_url: e.target.value })} placeholder="Lien (vidéo, Drive, site…)" data-testid="mission-form-attach-url" />
              <div className="flex items-center gap-2 flex-wrap">
                <input ref={createFileRef} type="file" onChange={(e) => uploadAttachment(e, setForm)} className="hidden" data-testid="mission-form-attach-file" accept="application/pdf,image/*" />
                <Button type="button" variant="outline" className="bg-white" onClick={() => createFileRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-2" /> {uploading ? "Téléversement…" : "Joindre un PDF / image"}
                </Button>
                {form.attachment_path && (
                  <span className="text-xs text-[#2A5B52] inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {form.attachment_name || "Fichier"}
                    <button type="button" onClick={() => setForm({ ...form, attachment_path: "", attachment_name: "" })}><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#5C6561]">Vous pouvez ajouter un lien ET un fichier en même temps.</p>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="mission-submit" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier la mission</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div className="text-sm text-[#5C6561]">Apprenant : <span className="font-medium text-[#1F2421]">{learnerName(editing.learner_id)}</span></div>
              <div><Label>Séance associée (optionnel)</Label>
                <Select value={editing.session_id || "none"} onValueChange={(v) => setEditing({ ...editing, session_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="mission-edit-session"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {editSessions.map(s => <SelectItem key={s.id} value={s.id}>Séance {s.number} — {s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Titre</Label><Input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="mission-edit-title" /></div>
              <div><Label>Description</Label><Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date de publication</Label>
                  <Input type="date" value={editing.publish_date || ""} onChange={(e) => setEditing({ ...editing, publish_date: e.target.value })} data-testid="mission-edit-publish" />
                </div>
                <div><Label>Date de rendu (facultatif)</Label>
                  <Input type="date" value={editing.deadline || ""} onChange={(e) => setEditing({ ...editing, deadline: e.target.value })} data-testid="mission-edit-deadline" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Pièces jointes (facultatif)</Label>
                <Input value={editing.attachment_url || ""} onChange={(e) => setEditing({ ...editing, attachment_url: e.target.value })} placeholder="Lien (vidéo, Drive, site…)" data-testid="mission-edit-attach-url" />
                <div className="flex items-center gap-2 flex-wrap">
                  <input ref={editFileRef} type="file" onChange={(e) => uploadAttachment(e, setEditing)} className="hidden" accept="application/pdf,image/*" />
                  <Button type="button" variant="outline" className="bg-white" onClick={() => editFileRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-2" /> {uploading ? "Téléversement…" : "Joindre un PDF / image"}
                  </Button>
                  {editing.attachment_path && (
                    <span className="text-xs text-[#2A5B52] inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {editing.attachment_name || "Fichier"}
                      <button type="button" onClick={() => setEditing({ ...editing, attachment_path: "", attachment_name: "" })}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#5C6561]">Vous pouvez ajouter un lien ET un fichier en même temps.</p>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="mission-edit-submit" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-3 gap-2 mb-4">
        <Select value={filters.learner_id} onValueChange={(v) => setFilters({ ...filters, learner_id: v })}>
          <SelectTrigger data-testid="filter-learner"><SelectValue placeholder="Apprenant" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les apprenants</SelectItem>
            {learners.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="todo">À faire</SelectItem>
            <SelectItem value="completed">Terminées</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="mission-sort"><SelectValue placeholder="Trier par" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récentes</SelectItem>
            <SelectItem value="learner">Par apprenant</SelectItem>
            <SelectItem value="publish">Par date de publication</SelectItem>
            <SelectItem value="deadline">Par date de rendu</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {sorted.length === 0 && <div className="dec-card p-6 text-center text-[#5C6561]">Aucune mission.</div>}
        {sorted.map((m) => (
          <div key={m.id} className="dec-card p-4 flex items-center gap-4" data-testid={`mission-row-${m.id}`}>
            <input type="checkbox" checked={m.completed} onChange={() => toggle(m)} className="w-5 h-5 accent-[#D95D39]" data-testid={`mission-toggle-${m.id}`} />
            <button type="button" onClick={() => setEditing({ ...m })} className="flex-1 min-w-0 text-left" data-testid={`mission-open-${m.id}`}>
              <div className="font-semibold truncate">{m.title}</div>
              <div className="text-xs text-[#5C6561] truncate">{learnerName(m.learner_id)}</div>
              <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-[#5C6561]">
                {m.publish_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Publiée le {fmtDate(m.publish_date)}</span>}
                {m.deadline && <span className="inline-flex items-center gap-1 text-[#C04E2D]"><Clock className="w-3 h-3" /> Rendu {fmtDate(m.deadline)}</span>}
              </div>
            </button>
            {m.attachment_url && (
              <button type="button" onClick={() => openUrl(m.attachment_url)} title="Lien" className="shrink-0 text-[#D95D39]" data-testid={`mission-attach-link-${m.id}`}>
                <Link2 className="w-4 h-4" />
              </button>
            )}
            {m.attachment_path && (
              <button type="button" onClick={() => openFile(m.attachment_path)} title="Pièce jointe" className="shrink-0 text-[#D95D39]" data-testid={`mission-attach-file-${m.id}`}>
                <Paperclip className="w-4 h-4" />
              </button>
            )}
            <Button variant="ghost" size="icon" title="Modifier" onClick={() => setEditing({ ...m })} data-testid={`mission-edit-${m.id}`}>
              <Pencil className="w-4 h-4 text-[#2A5B52]" />
            </Button>
            <Button variant="ghost" size="icon" title="Dupliquer vers un autre apprenant" onClick={() => duplicate(m)} data-testid={`mission-duplicate-${m.id}`}>
              <Copy className="w-4 h-4 text-[#D95D39]" />
            </Button>
            <Button variant="ghost" size="icon" title="Supprimer" onClick={() => remove(m.id)}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
